import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';
import ShareModal from '../components/ShareModal';
import CreateRoomModal from '../components/CreateRoomModal';
import PuzzleConfigForm, { getGridRecommendations } from '../components/PuzzleConfigForm';
import { createPuzzle } from '../services/puzzleApi';

function CreatePuzzle() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [imageData, setImageData] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [title, setTitle] = useState('');
  const [rows, setRows] = useState(3);
  const [columns, setColumns] = useState(3);
  const [pieceStyle, setPieceStyle] = useState('classic');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [createdPuzzle, setCreatedPuzzle] = useState(null);
  const [showRoomModal, setShowRoomModal] = useState(false);

  const handleImageSelect = (data) => {
    setError('');
    // Handle both old format (string) and new format (object with dimensions)
    if (typeof data === 'string') {
      setImageData(data);
      setImageDimensions(null);
    } else if (data === null) {
      setImageData(null);
      setImageDimensions(null);
    } else {
      setImageData(data.imageData);
      setImageDimensions({
        width: data.width,
        height: data.height,
        aspectRatio: data.aspectRatio,
      });
      // Auto-select first recommended grid if available
      const { recommended } = getGridRecommendations(data.aspectRatio);
      if (recommended.length > 0) {
        setRows(recommended[0].rows);
        setColumns(recommended[0].columns);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageData) {
      setError('Please select an image');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const puzzle = await createPuzzle({
        imageData,
        title: title || 'Untitled Puzzle',
        rows,
        columns,
        pieceStyle,
      });

      // Store puzzle info
      setCreatedPuzzle(puzzle);
      setIsCreating(false);

      // Guest users: show share modal
      if (!user) {
        const anonymousPuzzles = JSON.parse(localStorage.getItem('anonymousPuzzles') || '[]');
        // eslint-disable-next-line no-underscore-dangle
        anonymousPuzzles.push(puzzle._id);
        localStorage.setItem('anonymousPuzzles', JSON.stringify(anonymousPuzzles));
        return;
      }

      // Logged-in users: show room creation modal
      setShowRoomModal(true);
    } catch (err) {
      setError(err.message);
      setIsCreating(false);
    }
  };

  const handleShareModalClose = () => {
    // eslint-disable-next-line no-underscore-dangle
    navigate(`/play/${createdPuzzle._id}`);
  };

  const handleRoomModalClose = (roomCreated) => {
    setShowRoomModal(false);
    if (roomCreated) {
      // Room was created, navigate to play with room context
      // eslint-disable-next-line no-underscore-dangle
      navigate(`/play/${createdPuzzle._id}`);
    } else {
      // User skipped room creation, just play
      // eslint-disable-next-line no-underscore-dangle
      navigate(`/play/${createdPuzzle._id}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Share Modal for guest users */}
      {createdPuzzle && !user && (
        <ShareModal
          // eslint-disable-next-line no-underscore-dangle
          puzzleId={createdPuzzle._id}
          expiresAt={createdPuzzle.expiresAt}
          onClose={handleShareModalClose}
        />
      )}

      {/* Create Room Modal for logged-in users */}
      {showRoomModal && createdPuzzle && (
        <CreateRoomModal
          puzzle={createdPuzzle}
          onClose={handleRoomModalClose}
        />
      )}

      <h1 className="text-3xl font-bold text-puzzle-text mb-8">Create New Puzzle</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-puzzle-text mb-2">
            Puzzle Title (optional)
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="My Awesome Puzzle"
              maxLength={100}
              className="mt-1 w-full px-4 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-transparent bg-puzzle-surface text-puzzle-text"
            />
          </label>
        </div>

        <div>
          <span className="block text-sm font-medium text-puzzle-text mb-2">
            Upload Image
          </span>
          <ImageUploader onImageSelect={handleImageSelect} />
        </div>

        {imageData && (
          <PuzzleConfigForm
            imageData={imageData}
            imageDimensions={imageDimensions}
            rows={rows}
            columns={columns}
            pieceStyle={pieceStyle}
            onRowsChange={setRows}
            onColumnsChange={setColumns}
            onPieceStyleChange={setPieceStyle}
          />
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={!imageData || isCreating}
          className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
            !imageData || isCreating
              ? 'bg-puzzle-muted cursor-not-allowed text-puzzle-text-secondary'
              : 'bg-puzzle-blue hover:bg-blue-700'
          }`}
        >
          {isCreating ? 'Creating Puzzle...' : 'Create & Play'}
        </button>

        {!localStorage.getItem('token') && (
          <p className="text-sm text-puzzle-text-secondary text-center">
            Playing as guest. Login to save your puzzles and track stats.
          </p>
        )}
      </form>
    </div>
  );
}

export default CreatePuzzle;
